import React, { useMemo } from 'react';
import { Card, Statistic, Progress, Tooltip, Spin, Alert, Button } from 'antd';
import {
  DashboardOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import GaugeComponent from 'react-gauge-component';
import { obtenerEstadoAlerta, formatearValor } from '../../types/graficasPersonalizadas';
import './GraficaMedidor.scss';

const truncateValue = (value, suffix = '', maxLength = 8) => {
  const fullValue = `${value}${suffix ? ' ' + suffix : ''}`;

  if (!value || fullValue.length <= maxLength) {
    return { truncated: value, suffix: suffix, original: fullValue, isTruncated: false };
  }

  // Calculamos cuánto espacio necesitan las unidades (incluyendo espacio)
  const suffixLength = suffix ? suffix.length + 1 : 0; // +1 por el espacio
  const availableForNumber = maxLength - 3 - suffixLength; // -3 por los puntos "..."

  if (availableForNumber <= 0) {
    // Si no hay espacio suficiente, truncamos sin unidades y las ponemos en el tooltip
    const truncatedNumber = value.toString().substring(0, maxLength - 3) + '...';
    return {
      truncated: truncatedNumber,
      suffix: '',
      original: fullValue,
      isTruncated: true
    };
  }

  // Truncamos el número y agregamos las unidades al final
  const truncatedNumber = value.toString().substring(0, availableForNumber) + '...';
  const truncatedWithSuffix = truncatedNumber + (suffix ? ' ' + suffix : '');

  return {
    truncated: truncatedWithSuffix,
    suffix: '',
    original: fullValue,
    isTruncated: true
  };
};

// Componente para estadísticas con truncamiento automático
const TruncatedStatistic = ({ title, value, suffix, valueStyle, className }) => {
  const { truncated, suffix: newSuffix, original, isTruncated } = truncateValue(value, suffix);

  if (isTruncated) {
    return (
      <Tooltip title={original} placement="top">
        <div className={className}>
          <Statistic
            title={title}
            value={truncated}
            suffix={newSuffix}
            valueStyle={valueStyle}
          />
        </div>
      </Tooltip>
    );
  }

  return (
    <div className={className}>
      <Statistic
        title={title}
        value={value}
        suffix={suffix}
        valueStyle={valueStyle}
      />
    </div>
  );
};

const GraficaMedidor = ({
  grafica,
  datos,
  loading = false,
  error = null,
  onRefresh,
  className = ''
}) => {
  if (loading) {
    return (
      <Card
        className={`grafica-medidor ${className}`}
        title={
          <div className="grafica-medidor__header">
            <DashboardOutlined />
            <span>{grafica?.titulo || 'Cargando...'}</span>
          </div>
        }
      >
        <div className="grafica-medidor__loading">
          <Spin size="large" tip="Cargando datos..." />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        className={`grafica-medidor ${className}`}
        title={
          <div className="grafica-medidor__header">
            <DashboardOutlined />
            <span>{grafica?.titulo || 'Error'}</span>
          </div>
        }
      >
        <Alert
          message="Error al cargar datos"
          description={error}
          type="error"
          showIcon
          action={
            onRefresh && (
              <Tooltip title="Reintentar">
                <ReloadOutlined 
                  onClick={() => onRefresh(grafica._id, grafica.tipoGrafica)}
                  style={{ cursor: 'pointer' }}
                />
              </Tooltip>
            )
          }
        />
      </Card>
    );
  }

  // Validación mejorada de datos
  if (!datos ||
      typeof datos.valorActual !== 'number' ||
      isNaN(datos.valorActual) ||
      typeof datos.valorMinimo !== 'number' ||
      isNaN(datos.valorMinimo) ||
      typeof datos.valorMaximo !== 'number' ||
      isNaN(datos.valorMaximo) ||
      datos.valorMinimo >= datos.valorMaximo) {
    return (
      <Card
        className={`grafica-medidor grafica-medidor--sin-datos ${className}`}
        title={
          <div className="grafica-medidor__header">
            <DashboardOutlined />
            <span>{grafica?.titulo || 'Sin datos'}</span>
            {onRefresh && (
              <Tooltip title="Actualizar datos">
                <ReloadOutlined
                  onClick={() => onRefresh(grafica._id, grafica.tipoGrafica)}
                  className="grafica-medidor__refresh"
                />
              </Tooltip>
            )}
          </div>
        }
        style={{ minHeight: '400px' }} // Altura mínima consistente
      >
        <div className="grafica-medidor__no-data">
          <InfoCircleOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
          <h3>No hay datos disponibles</h3>
          <p>Este sensor no tiene lecturas válidas en este momento.</p>
          {onRefresh && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => onRefresh(grafica._id, grafica.tipoGrafica)}
              style={{ marginTop: '16px' }}
            >
              Intentar nuevamente
            </Button>
          )}
        </div>
      </Card>
    );
  }

  const { configuracion } = grafica;
  const {
    valorActual,
    valorMinimo,
    valorMaximo,
    unidades,
    porcentaje,
    estado,
    estadisticas,
    ultimaActualizacion
  } = datos;

  // Calcular valores para el gauge usando los datos correctos de la API
  const valorNormalizado = Math.max(valorMinimo, Math.min(valorMaximo, valorActual));
  const porcentajeCalculado = porcentaje || ((valorNormalizado - valorMinimo) / (valorMaximo - valorMinimo)) * 100;

  // Usar el estado que viene de la API o calcular uno por defecto
  const estadoFinal = estado || obtenerEstadoAlerta(valorActual, valorMinimo, valorMaximo);

  // Configurar colores del gauge según el estado
  const coloresGauge = useMemo(() => {
    const colorPrincipal = configuracion?.colorBarra || '#1890ff';

    // El puntero SIEMPRE usa el color personalizado
    // Solo el arco cambia según el estado para mantener la información visual de alerta
    switch (estadoFinal) {
      case 'peligro':
        return {
          needle: colorPrincipal, // Siempre color personalizado
          arc: ['#52c41a', '#faad14', '#ff4d4f'] // Arco estándar con colores de alerta
        };
      case 'advertencia':
        return {
          needle: colorPrincipal, // Siempre color personalizado
          arc: ['#52c41a', '#faad14', '#ff4d4f'] // Arco estándar con colores de alerta
        };
      case 'normal':
      default:
        return {
          needle: colorPrincipal, // Siempre color personalizado
          arc: ['#52c41a', '#faad14', '#ff4d4f'] // Arco estándar
        };
    }
  }, [estadoFinal, configuracion?.colorBarra]);

  const formatValue = (value) => {
    if (typeof value !== 'number' || isNaN(value)) {
      return '0';
    }
    return formatearValor(value, configuracion?.formatoValor || 'decimal');
  };

  // Configurar etiquetas del gauge según si se muestra porcentaje o valores reales
  const gaugeLabels = useMemo(() => {
    if (configuracion?.mostrarPorcentaje) {
      return {
        valueLabel: {
          formatTextValue: () => `${Math.round(porcentajeCalculado)}%`
        },
        tickLabels: {
          type: "outer",
          ticks: [
            { value: 0, valueConfig: { formatTextValue: () => '0%' } },
            { value: 25, valueConfig: { formatTextValue: () => '25%' } },
            { value: 50, valueConfig: { formatTextValue: () => '50%' } },
            { value: 75, valueConfig: { formatTextValue: () => '75%' } },
            { value: 100, valueConfig: { formatTextValue: () => '100%' } }
          ]
        }
      };
    } else {
      return {
        valueLabel: {
          formatTextValue: () => `${formatValue(valorActual)} ${unidades || ''}`
        },
        tickLabels: {
          type: "outer",
          ticks: [
            { value: 0, valueConfig: { formatTextValue: () => formatValue(valorMinimo) } },
            { value: 25, valueConfig: { formatTextValue: () => formatValue(valorMinimo + (valorMaximo - valorMinimo) * 0.25) } },
            { value: 50, valueConfig: { formatTextValue: () => formatValue(valorMinimo + (valorMaximo - valorMinimo) * 0.5) } },
            { value: 75, valueConfig: { formatTextValue: () => formatValue(valorMinimo + (valorMaximo - valorMinimo) * 0.75) } },
            { value: 100, valueConfig: { formatTextValue: () => formatValue(valorMaximo) } }
          ]
        }
      };
    }
  }, [configuracion?.mostrarPorcentaje, porcentajeCalculado, valorActual, valorMinimo, valorMaximo, unidades, formatValue]);

  return (
    <Card
      className={`grafica-medidor grafica-medidor--${estadoFinal} ${className}`}
      title={
        <div className="grafica-medidor__header">
          <DashboardOutlined />
          <span>{grafica.titulo}</span>
          {estadoFinal === 'peligro' && (
            <Tooltip title="Valor en rango peligroso">
              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            </Tooltip>
          )}
        </div>
      }
    >
      {grafica.descripcion && (
        <div className="grafica-medidor__description">
          <p>{grafica.descripcion}</p>
        </div>
      )}

      <div className="grafica-medidor__content">
        {/* Gauge principal */}
        <div className="grafica-medidor__gauge">
          <GaugeComponent
            value={porcentajeCalculado}
            type="semicircle"
            labels={gaugeLabels}
            arc={{
              colorArray: coloresGauge.arc,
              subArcs: [
                { limit: 60 },
                { limit: 80 },
                { limit: 100 }
              ],
              padding: 0.02,
              width: 0.3
            }}
            pointer={{
              elastic: true,
              animationDelay: 0,
              color: coloresGauge.needle,
              length: 0.80,
              width: 15
            }}
          />
        </div>

        {/* Estadísticas principales */}
        <div className="grafica-medidor__stats">
          <div className="grafica-medidor__main-value">
            <Statistic
              title={`Valor Actual (${unidades || ''})`}
              value={formatValue(valorActual)}
              valueStyle={{
                color: (!estado || estado === 'normal')
                  ? (configuracion?.colorBarra || '#52c41a')
                  : estadoFinal === 'peligro' ? '#ff4d4f'
                  : estadoFinal === 'advertencia' ? '#faad14'
                  : (configuracion?.colorBarra || '#52c41a'),
                fontSize: '2em'
              }}
            />
            
            {configuracion?.mostrarPorcentaje && (
              <div className="grafica-medidor__percentage">
                <Progress 
                  percent={Math.round(porcentaje)} 
                  status={estado === 'peligro' ? 'exception' : estado === 'advertencia' ? 'active' : 'success'}
                  strokeColor={coloresGauge.needle}
                />
              </div>
            )}
          </div>

          {/* Límites del sensor */}
          {configuracion?.mostrarLimites && (
            <div className="grafica-medidor__limits">
              <TruncatedStatistic
                title="Mínimo"
                value={formatValue(valorMinimo)}
                suffix={unidades}
                valueStyle={{ color: '#52c41a' }}
                className="grafica-medidor__limit"
              />
              <TruncatedStatistic
                title="Máximo"
                value={formatValue(valorMaximo)}
                suffix={unidades}
                valueStyle={{ color: '#ff4d4f' }}
                className="grafica-medidor__limit"
              />
            </div>
          )}

          {/* Estadísticas mensuales */}
          {estadisticas && (
            <div className="grafica-medidor__monthly-stats">
              <h4>Estadísticas del Mes</h4>
              <div className="grafica-medidor__monthly-grid">
                <TruncatedStatistic
                  title="Promedio"
                  value={formatValue(estadisticas.promedioMes || 0)}
                  suffix={unidades}
                  className="grafica-medidor__monthly-item"
                />
                <TruncatedStatistic
                  title="Mínimo"
                  value={formatValue(estadisticas.minimoMes || 0)}
                  suffix={unidades}
                  className="grafica-medidor__monthly-item"
                />
                <TruncatedStatistic
                  title="Máximo"
                  value={formatValue(estadisticas.maximoMes || 0)}
                  suffix={unidades}
                  className="grafica-medidor__monthly-item"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grafica-medidor__footer">
        <small>
          Última actualización: {ultimaActualizacion ? new Date(ultimaActualizacion).toLocaleString('es-ES') : 'No disponible'} • Promedio diario en tiempo real
        </small>
      </div>
    </Card>
  );
};

export default GraficaMedidor;